'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { HeartIcon } from '@heroicons/react/24/outline';
import type { PageUrlMap } from '@/types/blog';
import { useCustomerPanelUrlBuilder, useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { cardVariants, microHoverVariants } from '@/lib/animations';

interface FavoritesSummaryProps {
  count: number;
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  index?: number;
}

export default function FavoritesSummary({
  count,
  language = 'en',
  translations = {},
  pageUrls,
  defaultLanguage = 'en',
  index = 0,
}: FavoritesSummaryProps) {
  const t = useCustomerPanelTranslations(translations);
  const buildUrl = useCustomerPanelUrlBuilder(pageUrls, language, defaultLanguage);

  return (
    <motion.div 
      className="bg-gradient-to-br from-white to-pink-50/30 rounded-lg shadow-tour-card border border-pink-100 p-6 group hover:-translate-y-1 group-hover:shadow-2xl transition-all duration-300 transition-shadow duration-500 relative overflow-hidden"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      whileHover="hover"
    >
      {/* Decorative circle */}
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-pink-200/20 rounded-full blur-xl" />
      
      <div className="relative flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <motion.div 
            className="rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 p-4 shadow-lg"
            variants={microHoverVariants}
            whileHover="hover"
          >
            <HeartIcon className="h-7 w-7 text-white" />
          </motion.div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {t('customer_panel.dashboard.favorites_count', 'Favorites')}
            </p>
            <p className="text-3xl font-bold text-gray-900">{count}</p>
          </div>
        </div>
        <Link
          href={buildUrl('customer-favorites')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-alsero-first hover:text-white hover:bg-alsero-first rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 group/link"
          aria-label={t('customer_panel.dashboard.view_all_favorites', 'View all favorites')}
        >
          <span>{t('customer_panel.dashboard.view_all', 'View All')}</span>
          <motion.span
            className="inline-block"
            whileHover={{ x: 4 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            →
          </motion.span>
        </Link>
      </div>
    </motion.div>
  );
}

